import { useCallback, useEffect, useState, useRef } from 'react'
import Vapi from '@vapi-ai/web'

import { CallUI } from '@/features/callUI/CallUI'
import type { CallState } from '@/features/callUI/types'

const VAPI_PUBLIC_KEY = import.meta.env.VITE_VAPI_PUBLIC_KEY || ''

let globalActiveVapiCall: { agentId: string } | null = null
const globalVapiListeners = new Set<() => void>()

function notifyGlobalVapiChange() {
  globalVapiListeners.forEach((listener) => listener())
}

interface VapiCallButtonProps {
  vapiAgentId: string
  agentId: string
  disabled?: boolean
  variant?: 'compact' | 'full'
}

export function VapiCallButton({ vapiAgentId, agentId, disabled, variant = 'compact' }: VapiCallButtonProps) {
  const [callState, setCallState] = useState<CallState>('idle')
  const [isMuted, setIsMuted] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [, forceUpdate] = useState({})

  const [transcriptEvents, setTranscriptEvents] = useState<unknown[]>([])
  const [showEndedPanel, setShowEndedPanel] = useState(false)

  const vapiRef = useRef<Vapi | null>(null)
  const callEndedRef = useRef(false)

  useEffect(() => {
    const listener = () => forceUpdate({})
    globalVapiListeners.add(listener)
    return () => { globalVapiListeners.delete(listener) }
  }, [])

  const isAnotherCallActive = globalActiveVapiCall !== null && globalActiveVapiCall.agentId !== agentId

  const startCall = useCallback(async () => {
    if (callState !== 'idle' && callState !== 'ended') return

    setCallState('connecting')
    setConnectionError(null)
    setTranscriptEvents([])
    setShowEndedPanel(false)
    callEndedRef.current = false
    globalActiveVapiCall = { agentId }
    notifyGlobalVapiChange()

    try {
      const vapi = new Vapi(VAPI_PUBLIC_KEY)
      vapiRef.current = vapi

      vapi.on('call-start', () => {
        setCallState('connected')
        setStatusMessage('Connected — start talking')
        setConnectionError(null)
      })

      vapi.on('call-end', () => {
        callEndedRef.current = true
        setCallState('ended')
        setStatusMessage('Call ended')
        setShowEndedPanel(true)
        setIsMuted(false)
        globalActiveVapiCall = null
        notifyGlobalVapiChange()
        vapiRef.current = null
      })

      vapi.on('speech-start', () => {
        if (!callEndedRef.current) setStatusMessage('Agent speaking...')
      })

      vapi.on('speech-end', () => {
        if (!callEndedRef.current) setStatusMessage('Listening...')
      })

      vapi.on('message', (message: any) => {
        if (message.type === 'transcript' && message.transcriptType === 'final') {
          setTranscriptEvents((prev) => [
            ...prev,
            {
              type: 'turn',
              role: message.role === 'user' ? 'Callee' : 'Agent',
              text: message.transcript,
            },
          ])
        }
      })

      vapi.on('error', (error: any) => {
        console.error('VAPI error:', error)
        setConnectionError(error?.message || 'Call error')
      })

      if (vapiAgentId.startsWith('squad:')) {
        await vapi.start(undefined, undefined, vapiAgentId.slice(6))
      } else {
        await vapi.start(vapiAgentId)
      }
      setStatusMessage('Connecting...')
    } catch (error) {
      console.error('VAPI start error:', error)
      setConnectionError(error instanceof Error ? error.message : 'Failed to start call')
      setCallState('idle')
      globalActiveVapiCall = null
      notifyGlobalVapiChange()
      vapiRef.current = null
    }
  }, [callState, agentId, vapiAgentId])

  const endCall = useCallback(async () => {
    const vapi = vapiRef.current
    if (!vapi) return
    setCallState('disconnecting')
    try { vapi.stop() } catch { /* best-effort */ }
  }, [])

  const toggleMute = useCallback(() => {
    const vapi = vapiRef.current
    if (!vapi) return
    const next = !isMuted
    vapi.setMuted(next)
    setIsMuted(next)
  }, [isMuted])

  const closePanel = useCallback(() => {
    setShowEndedPanel(false)
    setCallState('idle')
    setStatusMessage(null)
    setTranscriptEvents([])
  }, [])

  useEffect(() => {
    return () => {
      const vapi = vapiRef.current
      if (vapi) {
        vapi.stop()
        globalActiveVapiCall = null
        notifyGlobalVapiChange()
        vapiRef.current = null
      }
    }
  }, [])

  return (
    <CallUI
      variant={variant}
      callState={callState}
      isMuted={isMuted}
      connectionError={connectionError}
      statusMessage={statusMessage}
      audioLevel={0}
      agentConnected={callState === 'connected'}
      transcriptEvents={transcriptEvents}
      startCall={startCall}
      endCall={endCall}
      toggleMute={toggleMute}
      closePanel={closePanel}
      disabled={disabled || isAnotherCallActive || !VAPI_PUBLIC_KEY}
      showEndedPanel={showEndedPanel}
    />
  )
}
